import styled, { css } from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { colorsPalettes, mixins } from "@similarweb/styles";
import { FlexRow } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import type { QuotaTagCreditsVisualState } from "../../types";

import type { QuotaTagSize, QuotaTagType } from "./QuotaTag.types";

const getQuotaTagTypeBackgroundColor = (type: QuotaTagType) => {
    switch (type) {
        case "light":
            return colorsPalettes.blue[100];
        case "regular":
            return colorsPalettes.blue[200];
        case "warning":
            return colorsPalettes.red[200];
        case "danger":
            return colorsPalettes.red[300];
        default:
            return colorsPalettes.blue[100];
    }
};

const getExtendedCreditsStyles = (creditsVisualState: QuotaTagCreditsVisualState, creditsDepleted: boolean) => {
    const cursor = creditsDepleted ? "not-allowed" : "pointer";

    switch (creditsVisualState) {
        case "default":
            return css`
                cursor: ${cursor};
                border: 1px solid ${colorsPalettes.bluegrey[300]};
                background-color: ${colorsPalettes.blue[100]};

                &:hover {
                    border: 1px solid ${colorsPalettes.bluegrey[300]};
                    background-color: ${colorsPalettes.blue[200]};
                }
            `;
        case "highUsage":
            return css`
                cursor: ${cursor};
                border: 1px solid ${colorsPalettes.yellow[200]};
                background-color: ${colorsPalettes.yellow[100]};

                &:hover {
                    border: 1px solid ${colorsPalettes.yellow[300]};
                    background-color: ${colorsPalettes.yellow[200]};
                }
            `;
        case "noCredits":
            return css`
                cursor: ${cursor};
                border: 1px solid ${colorsPalettes.bluegrey[300]};
                background-color: ${colorsPalettes.red[200]};

                &:hover {
                    border: 1px solid ${colorsPalettes.bluegrey[300]};
                    background-color: ${colorsPalettes.red[300]};
                }
            `;
        default:
            return css``;
    }
};

export const StyledQuotaTag = styled(FlexRow)<{
    type: QuotaTagType;
    size: QuotaTagSize;
    $extendedCredits?: boolean;
    $creditsVisualState?: QuotaTagCreditsVisualState;
    $creditsDepleted?: boolean;
}>`
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 16px;

    ${({ $extendedCredits, $creditsVisualState = "default", $creditsDepleted = false, type }) =>
        $extendedCredits
            ? getExtendedCreditsStyles($creditsVisualState, $creditsDepleted)
            : css`
                  border: 1px solid ${colorsPalettes.bluegrey[300]};
                  background-color: ${getQuotaTagTypeBackgroundColor(type)};
              `};

    padding: ${({ size }) => {
        switch (size) {
            case "sm":
                return "2px 6px";
            case "md":
                return "6px 8px";
            default:
                return "2px 6px";
        }
    }};
`;

export const StyledSWReactIcons = styled(SWReactIcons)`
    display: flex;
`;

export const StyledQuotaTagTitle = styled(Flex)<{ size: QuotaTagSize }>`
    user-select: none;

    ${({ size }) => {
        switch (size) {
            case "sm":
                return mixins.setFontVariant("queryBar1", colorsPalettes.midnight[400]);
            case "md":
                return mixins.setFontVariant("input2", colorsPalettes.midnight[400]);
            default:
                return mixins.setFontVariant("queryBar1", colorsPalettes.midnight[400]);
        }
    }};
`;
